import { muiGlyph } from "@/components/ui/mui-glyph";

// The glyphs the communities screens' maps and page-level actions draw, each
// path copied byte-for-byte from `@mui/icons-material` 9.2.0 (`<Name>.js`).
//
// Grouped by the surface that draws them, so a page ticket imports one file:
// the Show/Hide Map toggles (`Map` / `MapOutlined`), the map-type card
// (`Terrain`, `Satellite`), Reset map and Refresh (`RestartAlt`, `Refresh`),
// the fullscreen control (`Fullscreen` / `FullscreenExit`), the two external
// marks (`Launch` on the address link, `OpenInNew` on a link button), and
// `Home` on the full-page error's secondary action.
//
// Every one is declared at `SvgIcon`'s medium 24px box (`SvgIcon.js:75-78`).
// The slot it sits in sizes it where the donor's does — a small text button's
// 18px start icon, a medium button's 20px, the address link's `1rem` — by
// `className`, exactly as MUI's slot rules resize an `SvgIcon`.

/** `Map.js` — Hide Map. */
const MAP_PATH =
  "m20.5 3-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5M15 19l-6-2.11V5l6 2.11z";
/** `MapOutlined.js` — Show Map, and the map placeholders' 48px mark. */
const MAP_OUTLINED_PATH =
  "m20.5 3-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5M10 5.47l4 1.4v11.66l-4-1.4zm-5 .99 3-1.01v11.7l-3 1.16zm14 11.08-3 1.01V6.86l3-1.16z";
/** `Terrain.js` — the map-type card's Terrain face. */
const TERRAIN_PATH = "m14 6-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22z";
/** `Satellite.js` — the map-type card's Satellite face. */
const SATELLITE_PATH =
  "M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2M5 4.99h3C8 6.65 6.66 8 5 8zM5 12v-2c2.76 0 5-2.25 5-5.01h2C12 8.86 8.87 12 5 12m0 6 3.5-4.5 2.5 3.01L14.5 12l4.5 6z";
/** `RestartAlt.js` — Reset map. */
const RESTART_ALT_PATH =
  "M12 5V2L8 6l4 4V7c3.31 0 6 2.69 6 6 0 2.97-2.17 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93 0-4.42-3.58-8-8-8m-6 8c0-1.65.67-3.15 1.76-4.24L6.34 7.34C4.9 8.79 4 10.79 4 13c0 4.08 3.05 7.44 7 7.93v-2.02c-2.83-.48-5-2.94-5-5.91";
/** `Refresh.js` — the full-page error's "Refresh Page". */
const REFRESH_PATH =
  "M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4z";
/** `Fullscreen.js` / `FullscreenExit.js` — the map's fullscreen control. */
const FULLSCREEN_PATH = "M7 14H5v5h5v-2H7zm-2-4h2V7h3V5H5zm12 7h-3v2h5v-5h-2zM14 5v2h3v3h2V5z";
const FULLSCREEN_EXIT_PATH = "M5 16h3v3h2v-5H5zm3-8H5v2h5V5H8zm6 11h2v-3h3v-2h-5zm2-11V5h-2v5h5V8z";
/**
 * `OpenInNew.js` and `Launch.js`: one drawing, shipped by MUI under both names.
 * The donor reaches for each by its own name, so each keeps its own export.
 */
const OPEN_IN_NEW_PATH =
  "M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z";
/** `Home.js` — the full-page error's secondary action. The outlined twin is
 *  `mui-spec-icons.tsx`'s `MuiHomeOutlinedIcon`, another drawing. */
const HOME_PATH = "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z";

export const MuiMapIcon = muiGlyph("mui-map", MAP_PATH, "size-6");
export const MuiMapOutlinedIcon = muiGlyph("mui-map-outlined", MAP_OUTLINED_PATH, "size-6");
export const MuiTerrainIcon = muiGlyph("mui-terrain", TERRAIN_PATH, "size-6");
export const MuiSatelliteIcon = muiGlyph("mui-satellite", SATELLITE_PATH, "size-6");
export const MuiRestartAltIcon = muiGlyph("mui-restart-alt", RESTART_ALT_PATH, "size-6");
export const MuiRefreshIcon = muiGlyph("mui-refresh", REFRESH_PATH, "size-6");
export const MuiFullscreenIcon = muiGlyph("mui-fullscreen", FULLSCREEN_PATH, "size-6");
export const MuiFullscreenExitIcon = muiGlyph(
  "mui-fullscreen-exit",
  FULLSCREEN_EXIT_PATH,
  "size-6",
);
export const MuiOpenInNewIcon = muiGlyph("mui-open-in-new", OPEN_IN_NEW_PATH, "size-6");
export const MuiLaunchIcon = muiGlyph("mui-launch", OPEN_IN_NEW_PATH, "size-6");
export const MuiHomeIcon = muiGlyph("mui-home", HOME_PATH, "size-6");
